import { useEffect, useRef, useState } from "react";

import {
  refreshCmmdForgeRuntimeList,
  useCmmdForgeRuntimeListSnapshot,
  type CloudEnvironmentCatalogRow,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import { CloudExecutionTargetsSection } from "./CloudExecutionTargetsSection";
import { EnvironmentsPickerSection } from "./environmentsPickerSection";
import { DeleteEnvironmentDialog } from "./DeleteEnvironmentDialog";
import { useCloudConnectionRemoval } from "./useCloudConnectionRemoval";
import { useCloudWorkspaceActions } from "./useCloudWorkspaceActions";
import { useCmmdRuntimeRegistrySnapshot } from "./useCmmdRuntimeRegistrySnapshot";
import { forgeCloudEnvironmentLabelForRow } from "../../environments/forgeCloudEnvironmentLabel";
import { useForgeCloudEnvironmentNamingContext } from "../../environments/useForgeCloudEnvironmentNamingContext";
import { ForceUpdateEnvironmentDialog } from "./ForceUpdateEnvironmentDialog";

export const CONNECTIONS_MANUAL_REFRESH_TIMEOUT_MS = 15_000;

/**
 * Settings > Connections. Owns the Delete and Force update confirm dialogs, and
 * names their target with the same label the row shows.
 */
export function ConnectionsRuntimeSection() {
  const [isManualRefreshPending, setIsManualRefreshPending] = useState(false);
  const [forceUpdateTarget, setForceUpdateTarget] = useState<CloudEnvironmentCatalogRow | null>(
    null,
  );
  const manualRefreshTimerRef = useRef<number | null>(null);
  const cloudRuntimeSnapshot = useCmmdForgeRuntimeListSnapshot();
  const {
    runtimeRegistry,
    runtimeRegistryError,
    isLoadingRuntimeRegistry,
    refreshRuntimeRegistry,
  } = useCmmdRuntimeRegistrySnapshot();
  const {
    busyCloudProjectKey,
    busyCloudActionLabel,
    addCloudEnvironment,
    repairCloudWorkspace,
    recoverCloudEnvironment,
    runCloudRuntimeLifecycleAction,
    updateCloudRuntime,
  } = useCloudWorkspaceActions(refreshRuntimeRegistry);
  const connectionRemoval = useCloudConnectionRemoval();
  const namingContext = useForgeCloudEnvironmentNamingContext();
  // The LIVE row, not the one captured when the dialog opened: a rename lands
  // through a refetch, and the type-to-confirm gate has to move with it or it
  // keeps demanding a name the row no longer shows.
  const removalRuntimeId = connectionRemoval.target?.environment.runtimeId ?? null;
  const removalTargetEnvironment =
    cloudRuntimeSnapshot.environments.find(
      (environment) => environment.runtimeId === removalRuntimeId,
    ) ??
    connectionRemoval.target?.environment ??
    null;
  const removalTargetName =
    removalTargetEnvironment === null
      ? ""
      : forgeCloudEnvironmentLabelForRow(removalTargetEnvironment, namingContext);

  useEffect(() => {
    if (isManualRefreshPending && !cloudRuntimeSnapshot.isLoading && !isLoadingRuntimeRegistry) {
      setIsManualRefreshPending(false);
      if (manualRefreshTimerRef.current !== null) {
        window.clearTimeout(manualRefreshTimerRef.current);
        manualRefreshTimerRef.current = null;
      }
    }
  }, [cloudRuntimeSnapshot.isLoading, isLoadingRuntimeRegistry, isManualRefreshPending]);

  useEffect(() => {
    return () => {
      if (manualRefreshTimerRef.current !== null) {
        window.clearTimeout(manualRefreshTimerRef.current);
      }
    };
  }, []);

  const refreshConnections = () => {
    setIsManualRefreshPending(true);
    if (manualRefreshTimerRef.current !== null) {
      window.clearTimeout(manualRefreshTimerRef.current);
    }
    manualRefreshTimerRef.current = window.setTimeout(() => {
      manualRefreshTimerRef.current = null;
      setIsManualRefreshPending(false);
    }, CONNECTIONS_MANUAL_REFRESH_TIMEOUT_MS);
    refreshCmmdForgeRuntimeList();
    refreshRuntimeRegistry();
  };

  return (
    <>
      <CloudExecutionTargetsSection
        cloudSnapshot={cloudRuntimeSnapshot}
        registryDevices={runtimeRegistry?.devices ?? []}
        registryRuntimes={runtimeRegistry?.runtimes ?? []}
        registryError={runtimeRegistryError}
        registryIsLoading={isLoadingRuntimeRegistry}
        isRefreshing={isManualRefreshPending}
        busyCloudProjectKey={busyCloudProjectKey}
        busyCloudActionLabel={busyCloudActionLabel}
        onRefresh={refreshConnections}
        onAddCloudEnvironment={() => void addCloudEnvironment()}
        onRepairCloudWorkspace={(row) => void repairCloudWorkspace(row)}
        onRecoverCloudEnvironment={(environment, rows) =>
          void recoverCloudEnvironment(environment, rows)
        }
        onUpdateCloudRuntime={setForceUpdateTarget}
        onDeleteEnvironment={(environment, rows) => {
          if (rows.length === 0) {
            connectionRemoval.requestProjectlessRemoval(environment);
            return;
          }
          connectionRemoval.requestRemoval(environment, rows);
        }}
        onRemoveCloudProject={(row) => connectionRemoval.removeProject(row)}
        onRunCloudRuntimeLifecycleAction={(row, action) =>
          void runCloudRuntimeLifecycleAction(row, action)
        }
        onEnvironmentRenamed={refreshCmmdForgeRuntimeList}
      />
      <EnvironmentsPickerSection />
      <DeleteEnvironmentDialog
        open={connectionRemoval.target !== null}
        onOpenChange={connectionRemoval.setDialogOpen}
        environmentName={removalTargetName}
        hasRunningSprite={connectionRemoval.target !== null}
        hasProjects={connectionRemoval.target?.kind === "registered"}
        isDeleting={connectionRemoval.isRemoving}
        stepLabel={connectionRemoval.stepLabel}
        onConfirmDelete={connectionRemoval.confirmRemoval}
      />
      <ForceUpdateEnvironmentDialog
        open={forceUpdateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setForceUpdateTarget(null);
        }}
        environmentName={
          forceUpdateTarget === null
            ? "Environment"
            : forgeCloudEnvironmentLabelForRow(forceUpdateTarget, namingContext)
        }
        isUpdating={
          forceUpdateTarget !== null && busyCloudProjectKey === forceUpdateTarget.runtimeId
        }
        onConfirm={() => {
          if (forceUpdateTarget === null) return;
          void updateCloudRuntime(forceUpdateTarget).then((completed) => {
            if (completed) setForceUpdateTarget(null);
          });
        }}
      />
    </>
  );
}
